import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, map, tap} from 'rxjs';
import {InvoiceOrderStatusTranslationsService} from 'src/app/shared/translations';
import {InvoiceTicket, CancellationTicket, ReservationTicket} from '../../models';
import {environment} from '~environments/environment';

/**
 * Service for creating and updating invoice entity.
 */
@Injectable()
export class InvoiceService {
  public API_URL_CUSTOMERS: any = `${environment.envAPI}/customers`;

  public limitDisplayResults: number = 5;

  public dateOrder: string = 'order[orderDetails.invoiceDate]=desc';
  public statusOrder: string = 'type=';

  public invoiceList: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castInvoiceList: any = this.invoiceList.asObservable();

  public reservationsList: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castReservationsList: any = this.reservationsList.asObservable();

  public cancellationsList: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castCancellationsList: any = this.cancellationsList.asObservable();

  public invoiceListPage: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castInvoiceListPage: any = this.invoiceListPage.asObservable();

  public reservationsListPage: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castReservationsListPage: any = this.reservationsListPage.asObservable();

  public cancellationsListPage: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public castCancellationsListPage: any = this.cancellationsListPage.asObservable();

  public selectedInvoice: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castSelectedInvoice: any = this.selectedInvoice.asObservable();

  public numberOfReservations: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  public castNumberOfReservations: any = this.numberOfReservations.asObservable();

  constructor(private http: HttpClient, private invoiceTranslations: InvoiceOrderStatusTranslationsService) {
  }

  /**
   * Get all invoice tickets from a customer.
   */
  public getCustomerInvoices(customerId: number, itemsPerPage: number = this.limitDisplayResults): any {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/invoices?${this.dateOrder}&${this.statusOrder}Invoice&itemsPerPage=${itemsPerPage}`).pipe(
      map((response: Array<any>) => response['hydra:member'].map(ticket => new InvoiceTicket(ticket, this.invoiceTranslations)))
    );
  }

  /**
   * Get all reservation tickets from a customer.
   */
  public getCustomerReservations(customerId: number): any {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/reservations`).pipe(
      tap((response: Array<any>) => this.numberOfReservations.next(response['hydra:totalItems'])),
      map((response: Array<any>) => response['hydra:member'].map(ticket => new ReservationTicket(ticket, this.invoiceTranslations)))
    );
  }

  /**
   * Get all reservation tickets from a customer without changing it into a class.
   */
  public getCustomerReservationsRaw(customerId: number): any {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/reservations`).pipe(
      tap((response: Array<any>) => this.numberOfReservations.next(response['hydra:totalItems'])),
      map((response: Array<any>) => response['hydra:member'])
    );
  }

  /**
   * Get all cancellation tickets from a customer.
   */
  public getCustomerCancellations(customerId: number, itemsPerPage: number = this.limitDisplayResults): any {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/invoices?${this.dateOrder}&${this.statusOrder}Cancellation&itemsPerPage=${itemsPerPage}`).pipe(
      map((response: Array<any>) => response['hydra:member'].map(ticket => new CancellationTicket(ticket, this.invoiceTranslations)))
    );
  }

  /**
   * Get the endpoint and params for the ticket invoices.
   */
  public getTicketInvoicesEndpoint(customerId: number): any {
    const api = `${this.API_URL_CUSTOMERS}/${customerId}/invoices`;

    const params: Array<any> = [
      {'order[orderDetails.invoiceDate]': 'desc'},
      {type: 'Invoice'},
    ];

    return [api, params];
  }

  /**
   * Get the endpoint and params for the ticket cancellations.
   */
  public getTicketCancellationsEndpoint(customerId: number): any {
    const api = `${this.API_URL_CUSTOMERS}/${customerId}/invoices`;

    const params: Array<any> = [
      {'order[orderDetails.invoiceDate]': 'desc'},
      {type: 'Cancellation'},
    ];

    return [api, params];
  }

  /**
   * Set a new invoice list
   */
  public setInvoiceList(invoiceList: Array<any>): void {
    this.invoiceList.next(invoiceList);
  }

  /**
   * Set a new reservations list
   */
  public setReservationsList(reservationsList: any): void {
    this.reservationsList.next(reservationsList);
  }

  /**
   * Set a new cancellations list
   */
  public setCancellationsList(cancellationsList: Array<any>): void {
    this.cancellationsList.next(cancellationsList);
  }

  /**
   * Set a new invoice list
   */
  public setNumberOfReservations(numberOfReservations: number): void {
    this.numberOfReservations.next(numberOfReservations);
  }

  /**
   * Set a new reservations list
   */
  public setReservationsListPage(reservationsList: any): void {
    this.reservationsListPage.next(reservationsList);
  }

  /**
   * Set a new selected invoice
   */
  public setSelectedInvoice(invoice: any): void {
    this.selectedInvoice.next(invoice);
  }
}
